import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Truck, Envelope, Lock } from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Error al iniciar sesión');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <Toaster position="top-right" />
      
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-neutral-950 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center">
            <Truck size={48} weight="bold" className="text-white" />
            <span className="ml-3 text-3xl font-bold tracking-tight">SRoute</span>
          </div>
        </div>
        
        <div className="relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-4">
            Logistics Command Center
          </p>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-6 tracking-tight">
            Control total de tu flota, integrado con SAP y WhatsApp.
          </h1>
          <p className="text-neutral-400 text-base leading-relaxed max-w-md">
            Plataforma SaaS de gestión de transporte multi-empresa con visibilidad en tiempo real,
            evidencias digitales y automatización de comunicación.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4 pt-8 border-t border-neutral-800">
          <div>
            <p className="text-3xl font-bold">SAP</p>
            <p className="text-xs uppercase tracking-widest text-neutral-500 mt-1">R/3 · S/4HANA</p>
          </div>
          <div>
            <p className="text-3xl font-bold">Real-time</p>
            <p className="text-xs uppercase tracking-widest text-neutral-500 mt-1">WebSockets</p>
          </div>
          <div>
            <p className="text-3xl font-bold">Multi-empresa</p>
            <p className="text-xs uppercase tracking-widest text-neutral-500 mt-1">SaaS</p>
          </div>
        </div>

        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Truck size={40} weight="bold" className="text-blue-700" />
            <span className="ml-3 text-3xl font-bold tracking-tight text-neutral-900">SRoute</span>
          </div>

          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Sistema de Gestión Logística
            </p>
            <h2 className="text-3xl font-bold text-neutral-900 tracking-tight">
              Iniciar sesión
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              Ingresa a tu panel de control
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-medium uppercase tracking-widest text-neutral-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="w-full pl-11 pr-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent transition-shadow text-sm"
                  placeholder="admin@sroute.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-widest text-neutral-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full pl-11 pr-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent transition-shadow text-sm"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full py-3 bg-blue-700 text-white rounded-md font-medium hover:bg-blue-800 disabled:opacity-60 transition-colors text-sm uppercase tracking-widest"
            >
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
            </button>
          </form>

          <div className="mt-8 p-4 bg-neutral-50 border border-neutral-200 rounded-md">
            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
              Credenciales de prueba
            </p>
            <div className="space-y-1 text-xs text-neutral-700 font-mono">
              <p><strong>Super Admin:</strong> admin@sroute.com / Admin123!</p>
              <p><strong>Empresa:</strong> empresa1@test.com / Empresa123!</p>
              <p><strong>Logística:</strong> logistica@test.com / Logistica123!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
